import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import { createEmployeeFaltasApi } from './api';
import type { EmployeeFaltaDto, EmployeeFaltaInput, EmployeeFaltaListItem } from './types';

export function useEmployeeFaltasHooks(employeeId: number): CrudHooks<
  EmployeeFaltaListItem,
  EmployeeFaltaDto,
  EmployeeFaltaInput,
  string
> & {
  api: CrudApi<EmployeeFaltaListItem, EmployeeFaltaDto, EmployeeFaltaInput, string>;
} {
  const faltasApi = useMemo(() => createEmployeeFaltasApi(employeeId), [employeeId]);
  const queryKey = useMemo(() => ['employees', employeeId, 'faltas'] as const, [employeeId]);
  const qc = useQueryClient();

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<EmployeeFaltaListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query],
      queryFn: () => faltasApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (
    dataFalta: string | null | undefined,
  ): UseQueryResult<EmployeeFaltaDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', dataFalta],
      queryFn: () => faltasApi.get(dataFalta as string),
      enabled: dataFalta !== null && dataFalta !== undefined && dataFalta !== '',
    });

  const useCreate = (): UseMutationResult<EmployeeFaltaDto, Error, EmployeeFaltaInput> =>
    useMutation({
      mutationFn: (input) => faltasApi.create(input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
      },
    });

  const useUpdate = (): UseMutationResult<
    EmployeeFaltaDto,
    Error,
    { id: string; input: EmployeeFaltaInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => faltasApi.update(id, input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
      },
    });

  const useDelete = (): UseMutationResult<void, Error, string> =>
    useMutation({
      mutationFn: (dataFalta) => faltasApi.remove(dataFalta),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
      },
    });

  return { api: faltasApi, useList, useDetail, useCreate, useUpdate, useDelete };
}
